import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { PubsubService } from '../api/pubsub.service';

export interface OpenSharedItemFromNotificationContext {
  item_shared_requested?: boolean;
}

export interface OpenSharedItemFromNotificationConfig {
  /** item_type from notification (e.g. 'stakeholders', 'meldingen', 'stakeholders/contactpersonen', 'klanteisen', 'issues'). */
  itemType: string;
  /** Returns true when the current URL is this list (not a detail). */
  isOnListUrl: () => boolean;
  /** Fetches the item by id; should emit the item or null. context has e.g. item_shared_requested from notification/state. */
  fetchItem: (itemId: number, context?: OpenSharedItemFromNotificationContext) => Observable<any>;
  /** Opens the item (e.g. open modal / nav). context has e.g. item_shared_requested from notification/state. */
  openItem: (item: any, context?: OpenSharedItemFromNotificationContext) => void;
}

/**
 * Generic handler for "open shared item from in-app notification".
 * Use in list components so opening works both when navigating to the list (first load)
 * and when already on the list (same-URL navigation).
 */
@Injectable({ providedIn: 'root' })
export class OpenSharedItemFromNotificationService {

  /** Last "list key" (e.g. documenten, issues) when we handled open; used to avoid running tryOpenFromState twice when subRoute changes path (pubsub already handles that). */
  private lastListPathKey: string | null = null;

  /** When we open from pubsub, store (listKey, itemId) so the new component's tryOpenFromState (after subRoute navigation) skips the duplicate open. */
  private lastOpenedFromPubsub: { listPathKey: string; itemId: number } | null = null;
  private lastOpenedFromPubsubClearTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Normalize URL to path only: [project_code]/issues (or /sub/.../issues etc.).
   * Ignores query params (e.g. ?status=open), origin (e.g. http://localhost:4200), and trailing slashes
   * so /project/stakeholders and /project/stakeholders/ compare equal.
   */
  private getPathOnly(url: string): string {
    if (!url) return '';
    try {
      const withoutQuery = url.split('?')[0];
      let path: string;
      if (withoutQuery.startsWith('http://') || withoutQuery.startsWith('https://')) {
        path = new URL(withoutQuery).pathname;
      } else {
        path = withoutQuery;
      }
      // Normalize trailing slash so /project/stakeholders and /project/stakeholders/ compare equal
      return path === '/' ? path : path.replace(/\/+$/, '');
    } catch {
      const p = url.split('?')[0] ?? '';
      return p === '/' ? p : p.replace(/\/+$/, '');
    }
  }

  /**
   * Extract list key from path so /project/documenten and /project/sub/xxx/documenten compare equal.
   * Returns the list segment (e.g. documenten, issues); if path is a detail (e.g. /documenten/123) returns the list part.
   */
  private getListPathKey(path: string): string {
    if (!path) return '';
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 0) return '';
    const last = parts[parts.length - 1];
    // If last segment is numeric (detail view), the list key is the segment before it
    if (/^\d+$/.test(last) && parts.length >= 2) {
      return parts[parts.length - 2];
    }
    return last;
  }

  constructor(
    private router: Router,
    private pubsub: PubsubService,
  ) {}

  /**
   * Subscribe to pubsub + router and run open logic when notification targets this list.
   * @returns subscriptions to unsubscribe in ngOnDestroy.
   */
  subscribe(config: OpenSharedItemFromNotificationConfig): import('rxjs').Subscription[] {
    const subs: import('rxjs').Subscription[] = [];

    subs.push(
      this.pubsub.openSharedItemFromNotification$.subscribe((payload) => {
        if (
          payload?.group === 'shared_items' &&
          payload?.item_type === config.itemType &&
          payload?.item_id != null
        ) {
          const context: OpenSharedItemFromNotificationContext = {
            item_shared_requested: payload?.item_shared_requested,
          };
          const listPathKey = this.getListPathKey(this.getPathOnly(this.router.url));
          this.lastListPathKey = listPathKey;
          // Mark that we're opening from pubsub so the new component's tryOpenFromState (after subRoute navigation) skips duplicate.
          this.setLastOpenedFromPubsub(listPathKey, payload.item_id);
          this.fetchAndOpen(config, payload.item_id, context);
        }
      })
    );

    subs.push(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => {
          const currentPath = this.getPathOnly(this.router.url);
          const currentListKey = this.getListPathKey(currentPath);
          // Only run when list actually changed (e.g. dashboard → documenten). Skip same-list navigation
          // (e.g. /project/documenten → /project/sub/xxx/documenten when subRoute is true), already handled by pubsub.
          if (currentListKey !== this.lastListPathKey) {
            this.lastListPathKey = currentListKey;
            this.tryOpenFromState(config);
          }
        })
    );

    // First-load (e.g. dashboard → documenten): we land on this page with state, but history.state
    // may not be set yet when ngOnInit runs. Defer so we run after the current navigation completes.
    this.lastListPathKey = this.getListPathKey(this.getPathOnly(this.router.url));
    setTimeout(() => this.tryOpenFromState(config), 0);

    return subs;
  }

  private setLastOpenedFromPubsub(listPathKey: string, itemId: number): void {
    if (this.lastOpenedFromPubsubClearTimer) {
      clearTimeout(this.lastOpenedFromPubsubClearTimer);
    }
    this.lastOpenedFromPubsub = { listPathKey, itemId };
    this.lastOpenedFromPubsubClearTimer = setTimeout(() => {
      this.lastOpenedFromPubsub = null;
      this.lastOpenedFromPubsubClearTimer = null;
    }, 400);
  }

  private tryOpenFromState(config: OpenSharedItemFromNotificationConfig): void {
    const state = history.state;
    if (
      state?.extraData?.group !== 'shared_items' ||
      state?.item_id == null ||
      !config.isOnListUrl()
    ) {
      return;
    }
    const currentListKey = this.getListPathKey(this.getPathOnly(this.router.url));
    // Skip if we already opened this item from pubsub (e.g. subRoute navigated to a new component instance).
    if (
      this.lastOpenedFromPubsub &&
      this.lastOpenedFromPubsub.listPathKey === currentListKey &&
      this.lastOpenedFromPubsub.itemId === state.item_id
    ) {
      if (this.lastOpenedFromPubsubClearTimer) {
        clearTimeout(this.lastOpenedFromPubsubClearTimer);
        this.lastOpenedFromPubsubClearTimer = null;
      }
      this.lastOpenedFromPubsub = null;
      return;
    }
    const context: OpenSharedItemFromNotificationContext = {
      item_shared_requested: state?.item_shared_requested,
    };
    this.fetchAndOpen(config, state.item_id, context);
  }

  private fetchAndOpen(
    config: OpenSharedItemFromNotificationConfig,
    itemId: number,
    context?: OpenSharedItemFromNotificationContext
  ): void {
    config
      .fetchItem(itemId, context)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe((item) => {
        if (item) {
          config.openItem(item, context);
        }
      });
  }
}
